import { VoiceClient } from "@amazon-connect/voice";
import type { AmazonConnectProvider } from "@amazon-connect/core";
import type { ArgSpec, ClientModule } from "../types";

const CONTACT_ID: readonly ArgSpec[] = [
  { k: "contactId", type: "contactId", required: true },
];
const PARTICIPANT_ID: readonly ArgSpec[] = [
  { k: "participantId", type: "participantId", required: true },
];

/**
 * VoiceClient: hold/resume, outbound dialling, and voice enhancement. The hold/resume/hold-check
 * methods are participant-scoped only (no contactId) — see `sdk-signatures.md`.
 */
export const voiceModule: ClientModule = {
  id: "voice",
  label: "Voice",
  pkg: "@amazon-connect/voice",
  clientExport: "VoiceClient",
  kind: "observable",
  create: (provider: AmazonConnectProvider) =>
    new VoiceClient({ provider }) as unknown as Record<string, unknown>,
  events: [
    { name: "onSelfHold", off: "offSelfHold" },
    { name: "onSelfResume", off: "offSelfResume" },
    { name: "onParticipantHold", off: "offParticipantHold" },
    { name: "onParticipantResume", off: "offParticipantResume" },
    { name: "onCanResumeSelfChanged", off: "offCanResumeSelfChanged" },
    {
      name: "onCanResumeParticipantChanged",
      off: "offCanResumeParticipantChanged",
    },
    {
      name: "onVoiceEnhancementModeChanged",
      off: "offVoiceEnhancementModeChanged",
    },
  ],
  requests: [
    // getPhoneNumber is deprecated in favor of getInitialCustomerPhoneNumber (sdk-signatures.md).
    { name: "getPhoneNumber", args: CONTACT_ID, deprecated: true },
    { name: "getInitialCustomerPhoneNumber", args: CONTACT_ID },
    { name: "listDialableCountries", args: [] },
    { name: "getOutboundCallPermission", args: [] },
    { name: "isSelfOnHold", args: CONTACT_ID },
    { name: "isParticipantOnHold", args: PARTICIPANT_ID },
    { name: "canResumeSelf", args: CONTACT_ID },
    { name: "canResumeParticipant", args: PARTICIPANT_ID },
    { name: "getVoiceEnhancementMode", args: [] },
    { name: "getVoiceEnhancementPaths", args: [] },
    // createOutboundCall(phoneNumber: string, options?) — a phone number, not an "endpoint" object.
    {
      name: "createOutboundCall",
      args: [
        { k: "phoneNumber", type: "string", required: true },
        { k: "options", type: "json" },
      ],
      mutating: true,
    },
    { name: "holdSelf", args: CONTACT_ID, mutating: true },
    { name: "resumeSelf", args: CONTACT_ID, mutating: true },
    { name: "holdParticipant", args: PARTICIPANT_ID, mutating: true },
    { name: "resumeParticipant", args: PARTICIPANT_ID, mutating: true },
    { name: "conferenceParticipants", args: CONTACT_ID, mutating: true },
    // VoiceEnhancementMode = "VOICE_ISOLATION" | "NOISE_SUPPRESSION" | "NONE" (voice/lib-esm/types.d.ts).
    {
      name: "setVoiceEnhancementMode",
      args: [
        {
          k: "voiceEnhancementMode",
          type: "enum",
          required: true,
          options: ["VOICE_ISOLATION", "NOISE_SUPPRESSION", "NONE"],
        },
      ],
      mutating: true,
    },
  ],
};
